import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type UserRole = 'admin' | 'moderator' | 'user';

/**
 * Hook para buscar roles do usuário atual usando a nova tabela user_roles
 * FASE 2: Hook de teste (não substitui o sistema antigo ainda)
 */
export function useUserRole() {
  return useQuery({
    queryKey: ['user-role'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Busca os roles do usuário na nova tabela user_roles
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching user roles:', error);
        return null;
      }

      // Retorna array de roles (usuário pode ter múltiplos roles)
      return data?.map(r => r.role as UserRole) || [];
    },
    enabled: true,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });
}

/**
 * Hook simplificado para verificar se usuário é admin
 * Usa a nova tabela user_roles
 */
export function useIsAdmin() {
  const { data: roles, isLoading, error } = useUserRole();
  
  return {
    isAdmin: roles?.includes('admin') || false,
    isLoading,
    error
  };
}

/**
 * Hook para verificar se usuário tem um role específico
 */
export function useHasRole(requiredRole: UserRole) {
  const { data: roles, isLoading } = useUserRole();
  
  return {
    hasRole: roles?.includes(requiredRole) || false,
    isLoading
  };
}

/**
 * Hook LEGADO - busca is_admin da tabela profiles (sistema antigo)
 * Usado apenas para comparação nos testes da FASE 2
 * @deprecated Use useIsAdmin() em vez disso após validação
 */
export function useIsAdminLegacy() {
  return useQuery({
    queryKey: ['user-admin-legacy'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching legacy admin status:', error);
        return false;
      }

      return data?.is_admin || false;
    },
    enabled: true,
  });
}
